import React, { FC, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import type { AmountInputProps } from '../../../../../types';
import type { FreeBet } from 'app/api/services/freebets/types';
import type { ReduxState } from 'app/redux/types';

import { IconSize } from 'app/design-system/types/Icon';
import { tFunction, useTranslation } from 'app/lang';
import Dropdown, { ChangeHandler } from 'app/widgets/Dropdown/Single';

import { AccountType, SubmissionState } from '../../../../../constants';
import { selectAnyHasLockedState, selectSomeHasError } from '../../../../../selectors/entries';
import { selectSubmission } from '../../../../../selectors/submission';
import DropdownComponents from './DropdownComponents';

import dictionary from './AccountSelect.dictionary.json';

import styles from './AccountSelect.module.scss';

// ---

type PaymentOption = {
  isAccountType: true;
  label: string;
  value: AccountType;
};

const getMainPaymentOption = (t: tFunction): PaymentOption => ({
  isAccountType: true,
  label: t(dictionary.mainPaymentOptionLabel),
  value: AccountType.MAIN_ACCOUNT,
});

const getPledgeStakePaymentOption= (t: tFunction): PaymentOption => ({
  isAccountType: true,
  label: t(dictionary.pledgePaymentOptionLabel),
  value: AccountType.PLEDGE_STAKE_ACCOUNT,
});

export type ExtendedFreeBet = FreeBet & {
  disabled: boolean;
  disabledTitle: string | null;
};
export type AccountSelectOptionProps = PaymentOption | ExtendedFreeBet;

// ---

type StoreProps = {
  disabled: boolean;
};

export type AccountSelectProps = {
  account: AccountType;
  className?: string;
  disabledTitle?: {
    pledgeStakes?: string;
  };
  inputProps: AmountInputProps;
};

const AccountSelect: FC<AccountSelectProps & StoreProps> = ({
  disabled,
  account,
  className,
  inputProps,
  disabledTitle,
}) => {
  const { t } = useTranslation();
  const [menuPlacement, setMenuPlacement] = useState<'auto' | 'top' | 'bottom'>('auto');
  const [menuListBottom, setMenuListBottom] = useState<number | null>(null);
  const mainPaymentOption = getMainPaymentOption(t);
  const pledgeStakePaymentOption = getPledgeStakePaymentOption(t);
  const dropdownOptions: AccountSelectOptionProps[] = [mainPaymentOption, pledgeStakePaymentOption];
  const value = dropdownOptions.find((option) => 'value' in option && option.value === account);
  const menuPortalTarget: HTMLElement | null = document.querySelector('[data-id=sidebar]');
  const noOptionsMessage = useCallback(() => t(dictionary.noOptionsMessage), []);

  const onMenuOpenHandler = useCallback(() => {
    if (menuListBottom && menuListBottom < window.innerHeight && menuPlacement === 'top') {
      setMenuPlacement('auto');
    }
  }, [menuListBottom, menuPlacement]);

  const isOptionDisabledHandler = useCallback((option: AccountSelectOptionProps) => {
    if (isAccountTypeOption(option)) {
      return !!disabledTitle?.pledgeStakes && (
        option.value === AccountType.PLEDGE_STAKE_ACCOUNT
      );
    }

    return option.disabled;
  }, [disabledTitle]);

  const selectPaymentOptionHandler: ChangeHandler<
    AccountSelectOptionProps
  > = useCallback((option) => {
    const { onChange, value, name } = inputProps;

    if (!option || !('value' in option || 'id' in option)) {
      console.error('AccountSelect -> selectPaymentOptionHandler -> `option` not found', option);
      return;
    }

    if (isAccountTypeOption(option)) {
      if (account !== option.value) {
        onChange({ value, name, account: option.value });
      }
    }
  }, [account]);

  const components = DropdownComponents(
    disabledTitle?.pledgeStakes,
    menuListBottom,
    setMenuListBottom,
    menuPlacement,
  );

  useEffect(() => {
    if (menuListBottom && menuListBottom > window.innerHeight) {
      setMenuPlacement('top');
    }
  }, [menuListBottom]);

  return (
    <Dropdown<AccountSelectOptionProps>
              closeMenuOnScroll
              noOptionsMessage={noOptionsMessage}
              isSearchable={false}
              menuPlacement={menuPlacement}
              menuPortalTarget={menuPortalTarget}
              defaultValue={mainPaymentOption}
              options={dropdownOptions}
              menuPosition="fixed"
              className={classNames(styles.dropdown, className)}
              value={value}
              disabled={disabled}
              indicatorSize={IconSize.MEDIUM}
              maxMenuHeight={150}
              onMenuOpen={onMenuOpenHandler}
              onChange={selectPaymentOptionHandler}
              isOptionDisabled={isOptionDisabledHandler}
              components={components} />
  );
};

function isAccountTypeOption(option: AccountSelectOptionProps): option is PaymentOption {
  return 'isAccountType' in option;
}

const mapStateToProps = (state: ReduxState): StoreProps => {
  const hasEntriesErrors = selectSomeHasError(state) || selectAnyHasLockedState(state);

  return {
    disabled: selectSubmission(state) !== SubmissionState.AWAITING || hasEntriesErrors,
  };
};

export default connect(mapStateToProps)(AccountSelect);
